import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FeedbackApiBase } from './feedback-api-base';
import { DataEnvelope, GoogleDocExport, GoogleExportRequest, GoogleSheetExport } from '../models';

/**
 * Mirrors App\Http\Controllers\GoogleExportController — exports into the signed-in
 * user's Google Drive. The user must have connected Google first
 * ({@link GoogleConnectionService}); otherwise the API answers 409 with reason
 * `not_connected` or `reconnect_required`.
 */
@Injectable({ providedIn: 'root' })
export class GoogleExportService extends FeedbackApiBase {
  /**
   * POST /v1/feedbackrounds/{id}/export/google-sheet — every feedback item matching
   * the filters, in sort order, as a new Google Sheet in the user's Drive.
   */
  exportGoogleSheet(feedbackRoundId: string, request: GoogleExportRequest): Observable<GoogleSheetExport> {
    return this.http
      .post<DataEnvelope<GoogleSheetExport>>(
        this.url(`feedbackrounds/${feedbackRoundId}/export/google-sheet`),
        this.exportBody(request),
      )
      .pipe(map((response) => response.data));
  }

  /**
   * POST /v1/feedbackrounds/{id}/export/google-doc — every feedback item matching the
   * filters, in sort order, as a formatted Google Doc (screenshots embedded).
   */
  exportGoogleDoc(feedbackRoundId: string, request: GoogleExportRequest): Observable<GoogleDocExport> {
    return this.http
      .post<DataEnvelope<GoogleDocExport>>(
        this.url(`feedbackrounds/${feedbackRoundId}/export/google-doc`),
        this.exportBody(request),
      )
      .pipe(map((response) => response.data));
  }

  private exportBody(request: GoogleExportRequest): Record<string, unknown> {
    // The API takes several statuses comma-separated, as the list endpoint does.
    const status = Array.isArray(request.status) ? request.status.join(',') : request.status;
    // Unset filters are left out: Laravel turns "" into null, which fails validation.
    return Object.fromEntries(
      Object.entries({ ...request, status }).filter(
        ([, fieldValue]) => fieldValue !== undefined && fieldValue !== null && fieldValue !== '',
      ),
    );
  }
}
